import { useState, useEffect, useRef } from "react";
import { collection, onSnapshot, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import { BRAND, THEMES, STAFF } from "../../config/constants";
import { db, auth } from "../../firebase";
import { btnP, btnS } from "../../utils/styles";
import { sanitizeRichText } from "../../utils/safeHtml";

const CATEGORIES = ["Tax Tips", "NRA Guides", "Business", "IRS Updates", "Bookkeeping"];

export default function BlogPage({ theme }) {
  const t = THEMES[theme];
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null=list, object=editing
  const [showGenerate, setShowGenerate] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "blog_posts"), snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPosts(all.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    });
    return unsub;
  }, []);

  const filtered = filter === "all" ? posts :
    filter === "published" ? posts.filter(p => p.status === "published") :
    filter === "draft" ? posts.filter(p => p.status === "draft") :
    posts.filter(p => p.status === "review");

  const counts = { all: posts.length, published: posts.filter(p => p.status === "published").length, draft: posts.filter(p => p.status === "draft").length, review: posts.filter(p => p.status === "review").length };

  const inp = { background: t.inputBg || t.bg, border: "1px solid " + t.border, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, outline: "none", fontFamily: "'Outfit',sans-serif", width: "100%", boxSizing: "border-box" };

  // ── EDITOR VIEW ──
  if (editing) return (
    <BlogEditor post={editing} theme={theme} t={t} inp={inp}
      onBack={() => setEditing(null)}
      onSave={async (data) => {
        const saveData = { ...data, updatedAt: serverTimestamp() };
        if (data.status === "published" && !editing.publishedAt) saveData.publishedAt = serverTimestamp();
        if (editing.id) {
          await updateDoc(doc(db, "blog_posts", editing.id), saveData);
        } else {
          await addDoc(collection(db, "blog_posts"), { ...saveData, createdAt: serverTimestamp() });
        }
        setEditing(null);
      }}
      onDelete={editing.id ? async () => {
        if (!window.confirm("Delete this post?")) return;
        await deleteDoc(doc(db, "blog_posts", editing.id));
        setEditing(null);
      } : null}
    />
  );

  // ── AI GENERATE MODAL ──
  if (showGenerate) return (
    <GenerateModal theme={theme} t={t} inp={inp}
      onClose={() => setShowGenerate(false)}
      onGenerated={(post) => { setShowGenerate(false); setEditing(post); }}
    />
  );

  // ── LIST VIEW ──
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: t.text }}>Blog</div>
          <div style={{ fontSize: 13, color: t.textMuted }}>{counts.all} posts · {counts.published} published · {counts.draft} drafts</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setEditing({ title: "", slug: "", content: "", metaDescription: "", tags: [], category: "Tax Tips", author: "Bibek", status: "draft" })}
            style={{ ...btnS(t), padding: "8px 16px", fontSize: 12.5 }}>+ Write new</button>
          <button onClick={() => setShowGenerate(true)}
            style={{ ...btnP, padding: "8px 16px", fontSize: 12.5, background: "linear-gradient(135deg,#7F77DD,#534AB7)" }}>✦ AI generate</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
        {[["all", "All"], ["published", "Published"], ["draft", "Drafts"], ["review", "In review"]].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            style={{ padding: "5px 14px", borderRadius: 20, border: filter === k ? "1px solid " + BRAND.blue + "50" : "1px solid " + t.border, background: filter === k ? BRAND.blue + "15" : "transparent", color: filter === k ? BRAND.blue : t.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>
            {label} ({counts[k]})
          </button>
        ))}
      </div>

      {/* Post list */}
      {filtered.length === 0 ? (
        <div style={{ background: t.surface, border: "1px solid " + t.border, borderRadius: 12, padding: 40, textAlign: "center", color: t.textMuted }}>
          No posts yet. Write one or generate with AI.
        </div>
      ) : (
        <div style={{ background: t.surface, border: "1px solid " + t.border, borderRadius: 12, overflow: "hidden" }}>
          {filtered.map((p, i) => (
            <div key={p.id} onClick={() => setEditing(p)}
              style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderBottom: i < filtered.length - 1 ? "1px solid " + t.border : "none", cursor: "pointer", transition: "background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background = t.bg}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title || "Untitled"}</div>
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>
                  {p.aiGenerated ? "✦ AI" : ""} {p.author || "—"} · {p.category || "—"} · {p.createdAt?.seconds ? new Date(p.createdAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                </div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, flexShrink: 0,
                background: p.status === "published" ? BRAND.green + "18" : p.status === "review" ? "#f59e0b18" : t.bg,
                color: p.status === "published" ? BRAND.green : p.status === "review" ? "#f59e0b" : t.textMuted,
                border: "1px solid " + (p.status === "published" ? BRAND.green + "30" : p.status === "review" ? "#f59e0b30" : t.border)
              }}>{p.status === "published" ? "Published" : p.status === "review" ? "In Review" : "Draft"}</span>
              {p.tags?.length > 0 && <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>{p.tags.slice(0, 2).map(tag => (
                <span key={tag} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: BRAND.blue + "12", color: BRAND.blue }}>{tag}</span>
              ))}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── BLOG EDITOR ────────────────────────────────────────────────────────────
function BlogEditor({ post, theme, t, inp, onBack, onSave, onDelete }) {
  const [form, setForm] = useState({ title: post.title || "", slug: post.slug || "", content: post.content || "", metaDescription: post.metaDescription || "", tags: post.tags || [], category: post.category || "Tax Tips", author: post.author || "Bibek", status: post.status || "draft" });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState("");
  const editorRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Sync contentEditable → form.content
  const syncContent = () => {
    if (editorRef.current) set("content", editorRef.current.innerHTML);
  };

  // Auto-generate slug from title
  const genSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

  const handleSave = async (status) => {
    syncContent();
    setSaving(true);
    try {
      // Sanitise at save, not only at render: blog_posts is read by the public
      // marketing site, which injects content into innerHTML. Cleaning it here
      // means what is stored is already safe wherever it is later displayed.
      await onSave({ ...form, status: status || form.status, content: sanitizeRichText(editorRef.current?.innerHTML || form.content) });
    } catch (e) { alert("Save failed: " + e.message); }
    setSaving(false);
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) { set("tags", [...form.tags, tag]); }
    setTagInput("");
  };

  // AI assist actions
  const aiAssist = async (action) => {
    syncContent();
    const content = editorRef.current?.innerHTML || form.content;
    if (!content.trim()) return;
    setAiLoading(action);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch("https://us-central1-netsave-crm.cloudfunctions.net/aiBlogAssist", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ action, content }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "AI assist failed");
      if (data.content && editorRef.current) {
        editorRef.current.innerHTML = data.content;
        set("content", data.content);
      }
    } catch (e) { console.warn("AI assist:", e.message); }
    setAiLoading("");
  };

  // Toolbar commands
  const exec = (cmd, val) => { document.execCommand(cmd, false, val || null); editorRef.current?.focus(); };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: t.textMuted, fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
          <Icon name="arrow" size={14} color={t.textMuted} /> Back to posts
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          {onDelete && <button onClick={onDelete} style={{ padding: "7px 14px", borderRadius: 8, background: "transparent", border: "1px solid #f8717130", color: "#f87171", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>Delete</button>}
          <button onClick={() => handleSave("draft")} disabled={saving} style={{ ...btnS(t), padding: "7px 14px", fontSize: 12 }}>
            {saving ? <MiniSpinner size={12} color={t.textMuted} /> : "Save draft"}
          </button>
          <button onClick={() => handleSave("review")} disabled={saving} style={{ padding: "7px 14px", borderRadius: 8, background: "#f59e0b18", border: "1px solid #f59e0b30", color: "#f59e0b", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>
            Send to review
          </button>
          <button onClick={() => handleSave("published")} disabled={saving} style={{ ...btnP, padding: "7px 14px", fontSize: 12, background: "linear-gradient(135deg," + BRAND.green + ",#5a9a20)" }}>
            {saving ? <MiniSpinner size={12} color="#fff" /> : "Publish"}
          </button>
        </div>
      </div>

      {/* Meta fields */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 4 }}>Title</label>
          <input value={form.title} onChange={e => { set("title", e.target.value); if (!post.id) set("slug", genSlug(e.target.value)); }} style={inp} placeholder="Article title" />
        </div>
        <div>
          <label style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 4 }}>URL slug</label>
          <div style={{ display: "flex", alignItems: "center", ...inp, padding: 0, overflow: "hidden" }}>
            <span style={{ fontSize: 12, color: t.textMuted, padding: "9px 0 9px 12px", whiteSpace: "nowrap" }}>blog/</span>
            <input value={form.slug} onChange={e => set("slug", e.target.value)} style={{ ...inp, border: "none", padding: "9px 12px 9px 0", flex: 1 }} />
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 4 }}>Author</label>
          <select value={form.author} onChange={e => set("author", e.target.value)} style={inp}>
            {STAFF.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 4 }}>Category</label>
          <select value={form.category} onChange={e => set("category", e.target.value)} style={inp}>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 4 }}>Tags</label>
          <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap", ...inp, padding: "5px 8px", minHeight: 38 }}>
            {form.tags.map(tag => (
              <span key={tag} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: BRAND.blue + "15", color: BRAND.blue, display: "flex", alignItems: "center", gap: 4 }}>
                {tag} <span onClick={() => set("tags", form.tags.filter(t => t !== tag))} style={{ cursor: "pointer", fontSize: 13, lineHeight: 1 }}>×</span>
              </span>
            ))}
            <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              placeholder="+ add" style={{ border: "none", outline: "none", background: "transparent", color: t.text, fontSize: 12, fontFamily: "'Outfit',sans-serif", flex: 1, minWidth: 50, padding: "2px 0" }} />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 4 }}>Meta description (SEO)</label>
        <textarea value={form.metaDescription} onChange={e => set("metaDescription", e.target.value)} rows={2} style={{ ...inp, resize: "vertical" }} placeholder="Brief description for search engines (under 160 characters)" />
      </div>

      {/* AI Assist bar */}
      <div style={{ display: "flex", gap: 6, alignItems: "center", padding: "10px 14px", background: "#7F77DD12", borderRadius: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 14 }}>✦</span>
        <span style={{ fontSize: 12, color: "#534AB7", fontWeight: 700 }}>AI:</span>
        {["rewrite", "shorter", "expand", "seo", "cta"].map(a => (
          <button key={a} onClick={() => aiAssist(a)} disabled={!!aiLoading}
            style={{ padding: "3px 10px", borderRadius: 12, border: "1px solid #AFA9EC", background: aiLoading === a ? "#AFA9EC30" : "transparent", color: "#534AB7", fontSize: 12, cursor: aiLoading ? "not-allowed" : "pointer", fontFamily: "'Outfit',sans-serif" }}>
            {aiLoading === a ? "..." : a === "rewrite" ? "Rewrite" : a === "shorter" ? "Shorter" : a === "expand" ? "Expand" : a === "seo" ? "SEO" : "Add CTA"}
          </button>
        ))}
      </div>

      {/* Rich text editor */}
      <div style={{ border: "1px solid " + t.border, borderRadius: 10, overflow: "hidden", marginBottom: 12 }}>
        {/* Toolbar */}
        <div style={{ display: "flex", gap: 2, padding: "6px 10px", borderBottom: "1px solid " + t.border, background: t.bg, flexWrap: "wrap" }}>
          {[
            { cmd: "bold", label: "B", style: { fontWeight: 700 } },
            { cmd: "italic", label: "I", style: { fontStyle: "italic" } },
            { cmd: "underline", label: "U", style: { textDecoration: "underline" } },
          ].map(b => (
            <button key={b.cmd} onClick={() => exec(b.cmd)} style={{ padding: "4px 8px", borderRadius: 4, background: "transparent", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 13, fontFamily: "'Outfit',sans-serif", ...b.style }}>{b.label}</button>
          ))}
          <span style={{ padding: "0 4px", color: t.border }}>|</span>
          {[["formatBlock", "H2", "<h2>"], ["formatBlock", "H3", "<h3>"], ["formatBlock", "P", "<p>"]].map(([cmd, label, val]) => (
            <button key={label} onClick={() => exec(cmd, val)} style={{ padding: "4px 8px", borderRadius: 4, background: "transparent", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 12, fontFamily: "'Outfit',sans-serif" }}>{label}</button>
          ))}
          <span style={{ padding: "0 4px", color: t.border }}>|</span>
          <button onClick={() => exec("insertUnorderedList")} style={{ padding: "4px 8px", borderRadius: 4, background: "transparent", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 12, fontFamily: "'Outfit',sans-serif" }}>• List</button>
          <button onClick={() => exec("insertOrderedList")} style={{ padding: "4px 8px", borderRadius: 4, background: "transparent", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 12, fontFamily: "'Outfit',sans-serif" }}>1. List</button>
          <button onClick={() => exec("formatBlock", "<blockquote>")} style={{ padding: "4px 8px", borderRadius: 4, background: "transparent", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 12, fontFamily: "'Outfit',sans-serif" }}>Quote</button>
          <span style={{ padding: "0 4px", color: t.border }}>|</span>
          <button onClick={() => { const url = prompt("Enter URL:"); if (url) exec("createLink", url); }} style={{ padding: "4px 8px", borderRadius: 4, background: "transparent", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 12, fontFamily: "'Outfit',sans-serif" }}>Link</button>
        </div>

        {/* Editable area */}
        <div ref={editorRef} contentEditable suppressContentEditableWarning
          onBlur={syncContent}
          dangerouslySetInnerHTML={{ __html: sanitizeRichText(form.content) }}
          style={{ minHeight: 350, padding: "16px 20px", fontSize: 14, lineHeight: 1.7, color: t.text, outline: "none", fontFamily: "'Outfit',sans-serif", overflowY: "auto" }} />
      </div>
    </div>
  );
}

// ─── AI GENERATE MODAL ──────────────────────────────────────────────────────
function GenerateModal({ theme, t, inp, onClose, onGenerated }) {
  const [topic, setTopic] = useState("");
  const [length, setLength] = useState("medium");
  const [tone, setTone] = useState("friendly");
  const [author, setAuthor] = useState("Bibek");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const quickTopics = ["2025 tax deadlines for NRAs", "Form 8843 explained", "LLC vs S-Corp comparison", "Small business deductions", "Bookkeeping basics for startups", "IRS audit preparation tips", "Estimated tax payments guide", "Cryptocurrency tax reporting"];

  const generate = async () => {
    if (!topic.trim()) return;
    setGenerating(true); setError("");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch("https://us-central1-netsave-crm.cloudfunctions.net/generateBlogArticle", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ topic: topic.trim(), length, tone, author }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Generation failed");
      onGenerated({ id: data.id, title: data.title, slug: data.slug, content: data.content, metaDescription: data.metaDescription, tags: data.tags || [], category: data.category || "Tax Tips", author, status: "draft", aiGenerated: true });
    } catch (e) { setError(e.message); }
    setGenerating(false);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><Icon name="arrow" size={16} color={t.textMuted} /></button>
        <span style={{ fontSize: 18 }}>✦</span>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.text }}>Generate article with AI</div>
      </div>

      {/* Quick topics */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 8 }}>Quick topics</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {quickTopics.map(qt => (
            <button key={qt} onClick={() => setTopic(qt)}
              style={{ padding: "6px 12px", borderRadius: 20, border: "1px solid " + (topic === qt ? BRAND.blue + "50" : t.border), background: topic === qt ? BRAND.blue + "12" : "transparent", color: topic === qt ? BRAND.blue : t.textMuted, fontSize: 12, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>
              {qt}
            </button>
          ))}
        </div>
      </div>

      {/* Custom topic */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 4 }}>Or write your own topic</label>
        <input value={topic} onChange={e => setTopic(e.target.value)} style={inp} placeholder="e.g. How to report cryptocurrency gains for NRAs" />
      </div>

      {/* Options */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
        <div>
          <label style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 4 }}>Length</label>
          <select value={length} onChange={e => setLength(e.target.value)} style={inp}>
            <option value="short">Short (~500 words)</option>
            <option value="medium">Medium (~1000 words)</option>
            <option value="long">Long (~1500 words)</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 4 }}>Tone</label>
          <select value={tone} onChange={e => setTone(e.target.value)} style={inp}>
            <option value="friendly">Professional & friendly</option>
            <option value="formal">Formal & authoritative</option>
            <option value="simple">Simple & conversational</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 4 }}>Author</label>
          <select value={author} onChange={e => setAuthor(e.target.value)} style={inp}>
            {STAFF.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {error && <div style={{ marginBottom: 12, fontSize: 12.5, color: "#f87171", fontWeight: 600 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={generate} disabled={!topic.trim() || generating}
          style={{ ...btnP, flex: 1, justifyContent: "center", padding: "11px", fontSize: 14, background: "linear-gradient(135deg,#7F77DD,#534AB7)", opacity: !topic.trim() ? 0.5 : 1 }}>
          {generating ? <><MiniSpinner size={14} color="#fff" /> Generating article...</> : "✦ Generate article"}
        </button>
        <button onClick={onClose} style={{ ...btnS(t), padding: "11px 20px", fontSize: 13 }}>Cancel</button>
      </div>

      <div style={{ fontSize: 11.5, color: t.textMuted, textAlign: "center", marginTop: 10 }}>Article lands as a draft. Review and edit before publishing.</div>
    </div>
  );
}
